/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";


// Argon Dashboard 2 MUI example components
import SidenavItem from "examples/Sidenav/SidenavItem";
import SidenavFooter from "examples/Sidenav/SidenavFooter";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Argon Dashboard 2 MUI context
import { useArgonController, setMiniSidenav } from "context";
import { Box } from "@mui/material";
import useAdminUnreadCounts from "hooks/useAdminUnreadCounts";
import { ADMIN_UNREAD_MODULES } from "services/adminUnreadService";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  // console.log(routes)
  const [controller, dispatch] = useArgonController();
  const [collapseContoller, setCollapseController] = useState(null)
  const [searchQuery, setSearchQuery] = useState("");
  const { getCount } = useAdminUnreadCounts([
    ADMIN_UNREAD_MODULES.CRM,
    ADMIN_UNREAD_MODULES.SUPPORT_TICKET,
    ADMIN_UNREAD_MODULES.FEEDBACK,
    ADMIN_UNREAD_MODULES.PRODUCT_ENQ,
    ADMIN_UNREAD_MODULES.SERVICE_REVIEW,
    ADMIN_UNREAD_MODULES.TRANSACTION,
    ADMIN_UNREAD_MODULES.DEMO,
  ]);
  const { miniSidenav, darkSidenav, layout } = controller;
  const location = useLocation();
  const { pathname } = location;
  const itemName = pathname.split("/").slice(1)[0];

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    setSearchQuery("");
  }, [pathname]);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }
    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
     handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const getRouteBadgeCount = (key) => {
    const crmCount = getCount(ADMIN_UNREAD_MODULES.CRM);
    const supportCount = getCount(ADMIN_UNREAD_MODULES.SUPPORT_TICKET);
    const feedbackCount = getCount(ADMIN_UNREAD_MODULES.FEEDBACK);
    const productEnqCount = getCount(ADMIN_UNREAD_MODULES.PRODUCT_ENQ);
    const serviceReviewCount = getCount(ADMIN_UNREAD_MODULES.SERVICE_REVIEW);
    const transactionCount = getCount(ADMIN_UNREAD_MODULES.TRANSACTION);
    const demoCount = getCount(ADMIN_UNREAD_MODULES.DEMO);

    if (key === "crm") return crmCount;
    if (key === "demo") return demoCount;
    if (key === "support") return supportCount;
    if (key === "feedback") return feedbackCount;
    if (key === "product") return productEnqCount;
    if (key === "automatic-service-review") return serviceReviewCount;
    if (key === "transaction") return transactionCount;
    if (key === "Activities") return supportCount + serviceReviewCount;
    if (key === "useful") return productEnqCount;
    if (key === "finance") return transactionCount;
    return 0;
  };


  const searchTerms = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const isSearching = searchTerms.length > 0;
  const matchesSearch = (entry) => {
    const searchableText = `${entry.name || ""} ${entry.route || ""}`.toLowerCase();
    return searchTerms.every((term) => searchableText.includes(term));
  };
  // Search only the permission-filtered routes already supplied to the sidebar.
  const visibleRoutes = isSearching
    ? routes.flatMap((entry) => {
        if (entry.type !== "route") return [];
        if (matchesSearch(entry)) return [entry];
        const matchingChildren = entry.collapse?.filter(matchesSearch);
        return matchingChildren?.length ? [{ ...entry, collapse: matchingChildren }] : [];
      })
    : routes;

  // Render the visible routes, expanding matching groups while searching.
  const renderRoutes = visibleRoutes.map(({ type, name, icon, title, key, href, route,collapse,noCollapse }) => {
    let returnValue;
    // if (type === "route"){
    //   if (href){
    //     returnValue = (
    //       <Link href={href} key={key} target="_blank" rel="noreferrer">
    //         <SidenavItem
    //           name={name}
    //           icon={icon}
    //           active={key === itemName}
    //           noCollapse={noCollapse}
    //         />
    //       </Link>
    //     );
    //   } else {
    //     returnValue = (
    //       <NavLink to={route} key={key}>
    //         <SidenavItem name={name} icon={icon} active={key === itemName} />
    //       </NavLink>
    //     );
    //   }
    // }

     if (type === "route") {
      returnValue = href ? (
        <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }} >
          <SidenavCollapse
            myKey={key}
            color={color}
            name={name}
            icon={icon}
            active={route === pathname}
            noCollapse={noCollapse}
            badgeContent={getRouteBadgeCount(key)}
          />
        </Link>
      ) : collapse && collapse?.length ? (
        <SidenavCollapse
          color={color}
          key={key}
          myKey={key}
          name={name}
          icon={icon}
          handleCollapse={setCollapseController}
          collapseContoller={isSearching ? key : collapseContoller}
          noCollapse={noCollapse}
          collapse={collapse}
          badgeContent={getRouteBadgeCount(key)}
        >
          {collapse?.map(({ type, name, icon, title, noCollapse, key, route, href }) => href ? (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none", pl: 4 }}
            >
              <SidenavCollapse
                color={color}
                name={name}
                myKey={key}
                icon={icon}
                active={route === pathname}
                noCollapse={noCollapse}
                badgeContent={getRouteBadgeCount(key)}
              />
            </Link>
          ) : (
            <NavLink to={route} key={key}>
              <Box component="div" sx={{ ml: 1 }}>
                <SidenavCollapse
                  color={color}
                  key={key}
                  myKey={key}
                  name={name}
                  icon={icon}
                  active={route === pathname}
                  noCollapse={noCollapse}
                  badgeContent={getRouteBadgeCount(key)}
                />
              </Box>
            </NavLink>
          ))}
        </SidenavCollapse>
      )
        : (
          <NavLink to={route} key={key}>
            <SidenavCollapse
              color={color}
              key={key}
              myKey={key}
              name={name}
              icon={icon}
              active={route === pathname}
              noCollapse={noCollapse}
              badgeContent={getRouteBadgeCount(key)}
            />
          </NavLink>
        );
    } 

    else if (type === "title") {
      returnValue = (
        <ArgonTypography
          key={key}
          color={darkSidenav ? "white" : "dark"}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </ArgonTypography>
      );
    } else if (type === "divider") {
      returnValue = <Divider key={key} light={darkSidenav} />;
    }
    return returnValue;
  });

  return (
    <SidenavRoot id="sidenav" {...rest} variant="permanent" ownerState={{darkSidenav, miniSidenav, layout }}
     sx={{
    "& .MuiDrawer-paper": {
      overflow: "auto",
      "&::-webkit-scrollbar": {
        display: "none"
      },
      scrollbarWidth: "none",
      msOverflowStyle: "none"
    }
  }}
>
      <ArgonBox  pt={3} pb={1} px={4} textAlign="center">
        <ArgonBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <ArgonTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </ArgonTypography>
        </ArgonBox>

        <ArgonBox component={NavLink} to="/" display="fle" alignItems="center">
          {brand && (
            <ArgonBox component="img" src={brand} alt="Argon Logo" width="8rem" mr={0.25} />
          )}
          <ArgonBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <ArgonTypography
              component="h6"
              variant="button"
              fontWeight="medium"
              color={darkSidenav ? "white" : "dark"}
            >
              {brandName}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>

      {/* <Divider light={darkSidenav} /> */}

      <ArgonBox mx={2} mb={1}>
        {miniSidenav ? (
          <IconButton
            aria-label="Expand sidebar to search pages"
            onClick={() => setMiniSidenav(dispatch, false)}
            sx={{ color: darkSidenav ? "white" : "inherit" }}
          >
            <Icon>search</Icon>
          </IconButton>
        ) : (
          <ArgonInput
            fullWidth
            placeholder="Search pages..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setSearchQuery("");
            }}
            inputProps={{ "aria-label": "Search sidebar pages" }}
            startAdornment={
              <InputAdornment position="start"><Icon>search</Icon></InputAdornment>
            }
            endAdornment={searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear sidebar search" onClick={() => setSearchQuery("")}>
                  <Icon fontSize="small">close</Icon>
                </IconButton>
              </InputAdornment>
            ) : null}
          />
        )}
      </ArgonBox>

      {isSearching && visibleRoutes.length === 0 ? (
        <ArgonTypography role="status" variant="caption" color={darkSidenav ? "white" : "dark"} px={3} py={2}>
          No pages found. Try another name.
        </ArgonTypography>
      ) : (
        <List>{renderRoutes}</List>
      )}

      <ArgonBox pt={1} mt="auto" mb={2} mx={2}>
        {/* <SidenavFooter/> */}
        </ArgonBox>
    </SidenavRoot>
  );
}
// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
